import { Controller } from "@hotwired/stimulus"
import { getFormattedStreet } from "../utils/address-format.util"
import { isAbortError } from "../utils/fetch-error.util"
import { pluralText } from "../utils/plural-category.util"

// Connects to data-controller="legal-entity"
export default class extends Controller {
  static targets = [
    // data fields
    "country",
    "name",
    "city",
    "taxId",
    "vatId",
    "street",
    "postalCode",
    "registrationId",
    "itemTemplate",
    "registerText",
    "legalForm",
    "companyId",

    // API controls
    "hostUrl",
    "countryDecider",
    "companyNameList",
    "search",

    // UI elements
    "spinner",
    "clearButton",
    "error",
    "companyInfoSection",
    "registryNotice",
    "registryNoticeText",
    "searchHint",
    "summaryBanner",
    "summaryTitle",
    "vatTypeNotice"
  ]

  static values = {
    // Look the term up as soon as the form appears, without waiting for a
    // keystroke — set only where the name was prefilled for a NEW party.
    searchOnConnect: { type: Boolean, default: false },
    notFoundText: { type: String, default: "No results found" },
    registrationNumberLabel: { type: String, default: "Registration number" },
    minCharactersText: { type: String, default: "Please enter at least 3 characters to search" },
    searchingText: { type: String, default: "Searching..." },
    // Error copy arrives as values, like every other string this controller
    // renders — there is no i18n helper on a Stimulus controller.
    registryErrorText: {
      type: String,
      default: "Company register search failed. Try again or enter the details manually."
    },
    registryUnavailableText: {
      type: String,
      default: "The company register is temporarily unavailable. Try again shortly or enter the details manually."
    },
    // What the search can do per country, built by
    // RegistryHelper#registry_search_config: { countries: { "<id>": { level,
    // placeholder, notice } }, fallback, sources, vatIdLabel }. The level —
    // "full", "registration_id", "vat_id" or "none" — is the server's
    // (LegalEntityRegistry.search_level); this controller never decides what a
    // country can do, it only shows what it was told.
    registryConfig: { type: Object, default: {} },
    debounceDelay: { type: Number, default: 300 },
    // Prefill summary banner (Plan 3, Task 3). registerFallbackText is used when
    // the response carries no source_register_value (e.g. the Czech service
    // doesn't map one). fieldsCountTexts is the plural table built by
    // plural_texts_json in the view, so the correct sk/cs/uk form reaches this
    // controller without it needing any i18n logic of its own — same
    // convention as documents/items_counter_controller.
    lang: { type: String, default: "en" },
    registerFallbackText: { type: String, default: "" },
    fieldsCountTexts: { type: Object, default: {} }
  }

  connect() {
    this.abortController = null
    this.debounceTimeout = null
    this.repositionFrame = null
    // Every lookup is issued for one (term, countryId) pair. A response is only
    // applied while that pair is still what the form shows — otherwise a slow
    // registry can write a company the user has already navigated away from.
    this.requestSequence = 0
    // The company a lookup actually wrote into the dependent fields, so we can
    // tell when those values have gone stale.
    this.populatedIdentity = null
    this.activeIndex = -1
    this.currentResults = []
    this.currentResultContainer = null
    this.visualViewport = window.visualViewport
    this.repositionDropdown = this.repositionDropdown.bind(this)
    this.handleEntityTypeChange = this.handleEntityTypeChange.bind(this)
    this.handleVatPayerTypeChange = this.handleVatPayerTypeChange.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)

    // One debounce for every term. A numeric fast path looks attractive because
    // a finished IČO is an exact query — but every PREFIX of an IČO is numeric
    // too, so it fired a request per keystroke, each one proxied to an external
    // registry (and the last costing two upstream round-trips via the ID merge).
    // abort() only closes the client socket; the server work still happens.
    this.debouncedSearch = () => {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => this.searchAction(), this.debounceDelayValue)
    }

    this.updateStatus(false)

    if (this.hasCountryDeciderTarget) {
      this._countryChangeHandler = () => this.onCountryChange()
      this.countryDeciderTarget.addEventListener("change", this._countryChangeHandler)
    }

    // Match the VAT-ID placeholder to the selected country's format. Also listen
    // on the address country target(s) directly, since forms without a
    // countryDecider (party/contact) drive the country selection there.
    this._vatPlaceholderHandler = () => this.updateVatIdPlaceholder()
    this.countryTargets.forEach((el) => el.addEventListener("change", this._vatPlaceholderHandler))
    this.updateVatIdPlaceholder()

    this.element.addEventListener("change", this.handleEntityTypeChange)
    // Delegated, so it still catches the radios after the navigate refresh
    // replaces the vat_payer_type frame.
    this.element.addEventListener("change", this.handleVatPayerTypeChange)
    this.searchTargets.forEach((input) => input.addEventListener("keydown", this.handleKeydown))
    window.addEventListener("resize", this.repositionDropdown)
    window.addEventListener("scroll", this.repositionDropdown, true)

    if (this.visualViewport) {
      this.visualViewport.addEventListener("resize", this.repositionDropdown)
      this.visualViewport.addEventListener("scroll", this.repositionDropdown)
    }

    if (document.getElementById("address_name_error")) {
      this.#showCompanyInfoSection()
    }

    this.#syncRegistryNotice()

    // Opened from the "no results — create <term>" call to action: the term is
    // already in the name field, so the registry lookup the user would have
    // triggered by typing runs on its own. search() bails out by itself for an
    // unsupported country, a physical person, or an empty term.
    if (this.searchOnConnectValue) this.search()
  }

  disconnect() {
    this.#invalidatePendingSearch()
    if (this.repositionFrame) window.cancelAnimationFrame(this.repositionFrame)

    if (this._countryChangeHandler && this.hasCountryDeciderTarget) {
      this.countryDeciderTarget.removeEventListener("change", this._countryChangeHandler)
    }

    if (this._vatPlaceholderHandler) {
      this.countryTargets.forEach((el) => el.removeEventListener("change", this._vatPlaceholderHandler))
    }

    this.element.removeEventListener("change", this.handleEntityTypeChange)
    this.element.removeEventListener("change", this.handleVatPayerTypeChange)
    this.searchTargets.forEach((input) => input.removeEventListener("keydown", this.handleKeydown))
    window.removeEventListener("resize", this.repositionDropdown)
    window.removeEventListener("scroll", this.repositionDropdown, true)

    if (this.visualViewport) {
      this.visualViewport.removeEventListener("resize", this.repositionDropdown)
      this.visualViewport.removeEventListener("scroll", this.repositionDropdown)
    }
  }

  search() {
    const searchValue = this.#searchValue()

    // Runs before every early return: the previously looked-up company's data is
    // stale the moment the term moves away from it, whatever happens next.
    this.#discardStaleRegistryData()

    // If physical person is selected, do not make request to legal entity registry
    if (this.#isPhysicalPersonSelected()) {
      this.#syncInputAffordances()
      return
    }

    this.#syncInputAffordances()

    // If we're in an unsupported country, do nothing (leave manual input alone)
    if (!this.isSupportedCountry()) {
      return
    }

    // At this point, the country IS supported:
    // — empty term? clear the dropdown and reset fields
    if (!searchValue) {
      this.clearSearch()
      return
    }

    // non-empty term? debounce your API call
    this.debouncedSearch()
  }

  /**
   * Checks if physical person entity type is currently selected
   * @returns {boolean} - True if physical person is selected
   */
  #isPhysicalPersonSelected() {
    const physicalPersonRadio = this.element.querySelector("input[name='entity_type'][value='physical_person']")
    return physicalPersonRadio?.checked
  }

  async searchAction() {
    const term = this.#searchValue()
    const countryId = this.#selectedCountryId()

    // Whatever this call decides to do, it supersedes the queued/in-flight one —
    // including when it bails out below, which would otherwise strand the
    // spinner on a request whose response is now discarded.
    this.#invalidatePendingSearch()

    if (!term || !countryId) {
      this.updateStatus(false)
      return
    }

    if (term.length < 3) {
      this.updateStatus(false)
      this.showMinCharactersMessage()
      return
    }

    const workspaceId = this.registryConfigValue.workspaceId
    const workspaceParam = workspaceId ? `&workspace_id=${encodeURIComponent(workspaceId)}` : ""
    const url = `/legal_entity_registry/search?country_id=${countryId}&search_type=auto&search_value=${encodeURIComponent(term)}${workspaceParam}`

    this.abortController = new AbortController()
    const request = { id: this.requestSequence, term, countryId, input: this.#activeSearchInput() }

    this.updateStatus(true)

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })
      if (!this.#isCurrentRequest(request)) return

      // 503 is our endpoint reporting that the registry ITSELF is unreachable.
      // That is not "this company does not exist", and must not be shown as it.
      if (response.status === 503) {
        this.updateStatus(false)
        this.showError("registry_unavailable")
        return
      }

      // The workspace's daily lookup quota (LegalEntityRegistry::LookupQuota).
      // A 429 from the per-IP throttle carries no such code and stays a
      // generic failure — "daily limit" would be untrue for it.
      if (response.status === 429) {
        const body = await response.json().catch(() => ({}))
        if (!this.#isCurrentRequest(request)) return

        this.updateStatus(false)
        this.showError(body.error === "registry_lookup_limit" ? "registry_lookup_limit" : "registry_error")
        return
      }

      if (!response.ok) {
        const errorText = await response.text()
        console.error("[legal-entity] Response error:", errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }
      const data = await response.json()
      if (!this.#isCurrentRequest(request)) return

      this.updateStatus(false)
      // Handle single result vs array of results
      const results = Array.isArray(data) ? data : [data]
      this.#renderOptions(results, this.companyNameListTarget, request)
    } catch (err) {
      // Deliberately NOT re-raised: this hits the third-party business registry,
      // whose outages are not our defect, and the user already gets a clear
      // message. Re-raising would flood monitoring with someone else's downtime.
      // Our own endpoints do re-raise — see fetch-error.util.
      //
      // No `finally` for updateStatus, deliberately, though main's copy of this
      // catch had one: the two guards below RETURN, and a finally still runs on
      // the way out. An abort here means a newer lookup superseded this one and
      // has already called updateStatus(true), so clearing in finally would wipe
      // the spinner belonging to the request still in flight.
      if (isAbortError(err)) return
      if (!this.#isCurrentRequest(request)) return

      console.error("[legal-entity] Fetch error", err)
      this.updateStatus(false)
      this.showError("registry_error")
    }
  }

  // Cancels whatever lookup is queued or in flight and makes any response that
  // is still on the wire stale, so it can never be applied.
  #invalidatePendingSearch() {
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = null
    this.abortController?.abort()
    this.abortController = null
    this.requestSequence += 1
  }

  // A response may only be applied while the form still shows the exact
  // (term, countryId) pair the request was issued for.
  #isCurrentRequest(request) {
    if (request.id !== this.requestSequence) return false
    if (request.countryId !== this.#selectedCountryId()) return false

    return request.term === (request.input?.value.trim() || "")
  }

  updateStatus(loading) {
    this.spinnerTarget.classList.toggle("d-none", !loading)
    this.#syncInputAffordances(loading)

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("d-none")
    }

    if (loading) {
      if (this.hasCompanyNameListTarget) {
        this.clearOptions(this.companyNameListTarget)
        // Show searching message
        const li = document.createElement("li")
        li.textContent = this.searchingTextValue
        li.classList.add("dropdown-item", "dropdown-item--empty")
        this.companyNameListTarget.appendChild(li)
        this.companyNameListTarget.classList.add("show")
        this.#positionDropdown(this.companyNameListTarget)
      }
    }
  }

  isSupportedCountry() {
    return this.#countryCopy().level !== "none"
  }

  // The selected country's entry in the server's config; a country it does not
  // list has no register.
  #countryCopy() {
    const config = this.registryConfigValue
    const copy = config.countries?.[String(this.#selectedCountryId())]
    return copy || config.fallback || { level: "none", placeholder: "", notice: null }
  }

  // Is a register lookup on the table at all? Both facts live on this
  // controller, so it owns every piece of register-related copy — splitting the
  // ownership across two controllers would mean two writers on one element.
  #registerAvailable() {
    return this.isSupportedCountry() && !this.#isPhysicalPersonSelected()
  }

  // What the search can do here, said under the field: only by CUI, only by
  // VAT id, or not at all. A full register says nothing — the placeholder
  // already promises ID or name. A private person never gets a register
  // lookup, so never a sentence about one.
  #syncRegistryNotice() {
    if (!this.hasRegistryNoticeTarget) return

    const notice = this.#isPhysicalPersonSelected() ? null : this.#countryCopy().notice
    if (this.hasRegistryNoticeTextTarget) this.registryNoticeTextTarget.textContent = notice || ""
    this.registryNoticeTarget.classList.toggle("d-none", !notice)
  }

  onCountryChange() {
    // The queued/in-flight lookup was issued against the previous country — its
    // results would be another country's companies.
    this.#invalidatePendingSearch()
    if (this.hasCompanyNameListTarget) {
      this.clearOptions(this.companyNameListTarget)
    }

    // Sync the address country field with the virtual country field
    if (this.hasCountryTarget && this.hasCountryDeciderTarget) {
      const countryId = this.countryDeciderTarget.value
      this.countryTargets.forEach((el) => this.#setSelectValue(el, countryId))
    }

    this.#discardStaleRegistryData()

    if (!this.isSupportedCountry()) {
      this.#showCompanyInfoSection()
      this.#requireManualEntry()
    }

    this.updateStatus(false)
    this.updateVatIdPlaceholder()
    this.#syncRegistryNotice()

    // The country select also carries `change->legal-entity#search`, and Stimulus
    // starts its binding observer BEFORE controller.connect() registers this
    // handler — so search() has already queued a lookup by the time we get here,
    // and #invalidatePendingSearch() above just cancelled it. Cancelling the OLD
    // country's lookup is right; leaving the user with none is not, so re-arm one
    // for the country now selected.
    if (this.#registerAvailable() && this.#searchValue()) {
      this.debouncedSearch()
    }

    // Everything above only invalidates THIS controller's own stale registry
    // data. contacts--register-diff runs a second, independent lookup against
    // the same registry and holds on to its result, so it has to hear about the
    // change too — same document-level CustomEvent convention as
    // "registry:selected" below, which contacts--disclosure already listens on.
    document.dispatchEvent(
      new CustomEvent("registry:country-changed", { detail: { countryId: this.#selectedCountryId() } })
    )
  }

  // The identifier and address fields belong to the company a lookup put there.
  // Once the lookup identity (term + country) moves away from that company those
  // values are stale, and clearing them only on an EMPTY term was not enough:
  // selecting company A and then editing the name to an unmatched B left A's
  // IČO, DIČ and address attached to the record all the way through Save.
  #discardStaleRegistryData() {
    if (!this.populatedIdentity) return
    if (
      this.populatedIdentity.name === this.#searchValue() &&
      this.populatedIdentity.countryId === this.#selectedCountryId()
    ) {
      return
    }

    this.populatedIdentity = null
    this.#clearDependentFields()
  }

  // Set the VAT-ID field's placeholder to the selected country's example format
  // (data-vat-placeholder on the chosen <option>), falling back to the field's
  // original placeholder for countries we have no format for.
  updateVatIdPlaceholder() {
    if (!this.hasVatIdTarget) return

    const select = this.#activeCountrySelect()
    const option = select?.options?.[select.selectedIndex]
    const countryPlaceholder = option?.dataset?.vatPlaceholder

    this.vatIdTargets.forEach((el) => {
      if (el.dataset.defaultVatPlaceholder === undefined) {
        el.dataset.defaultVatPlaceholder = el.getAttribute("placeholder") || ""
      }
      el.setAttribute("placeholder", countryPlaceholder || el.dataset.defaultVatPlaceholder)
    })
  }

  #activeCountrySelect() {
    if (this.hasCountryDeciderTarget) return this.countryDeciderTarget
    if (this.hasCountryTarget) return this.countryTargets[0]
    return null
  }

  handleEntityTypeChange(event) {
    if (event.target?.name !== "entity_type") return
    this.#syncInputAffordances()
    this.#syncRegistryNotice()
  }

  // The user answered the question the § 7/§ 7a or unknown-type notice asked.
  handleVatPayerTypeChange(event) {
    if (event.target?.name !== "party[vat_payer_type]") return
    this.#hideVatTypeNotice()
  }

  handleKeydown(event) {
    const container = this.#activeDropdownContainer()
    if (!container) return

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault()
        const items = this.#selectableItems(container)
        if (items.length === 0) return
        this.activeIndex = this.activeIndex < items.length - 1 ? this.activeIndex + 1 : 0
        this.#highlightActiveItem(items)
        break
      }
      case "ArrowUp": {
        event.preventDefault()
        const items = this.#selectableItems(container)
        if (items.length === 0) return
        this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : items.length - 1
        this.#highlightActiveItem(items)
        break
      }
      case "Enter": {
        if (this.activeIndex < 0) return
        event.preventDefault()
        const items = this.#selectableItems(container)
        if (this.activeIndex < items.length) {
          items[this.activeIndex].click()
        }
        break
      }
      case "Escape": {
        event.preventDefault()
        this.clearOptions(container)
        this.activeIndex = -1
        break
      }
    }
  }

  #activeDropdownContainer() {
    if (this.hasCompanyNameListTarget && this.companyNameListTarget.classList.contains("show")) {
      return this.companyNameListTarget
    }
    return null
  }

  #selectableItems(container) {
    return Array.from(container.querySelectorAll(".dropdown-item:not(.dropdown-item--empty)"))
  }

  #highlightActiveItem(items) {
    items.forEach((item, index) => {
      item.classList.toggle("dropdown-item--active", index === this.activeIndex)
      item.setAttribute("aria-selected", index === this.activeIndex ? "true" : "false")
    })
    if (this.activeIndex >= 0 && this.activeIndex < items.length) {
      items[this.activeIndex].scrollIntoView?.({ block: "nearest" })
    }
  }

  #renderOptions(data, container, request = null) {
    this.clearOptions(container)
    this.activeIndex = -1

    if (!Array.isArray(data) || data.length === 0) {
      const li = document.createElement("li")

      li.textContent = this.notFoundTextValue

      li.classList.add("dropdown-item", "dropdown-item--empty")

      li.addEventListener("click", () => {
        this.clearOptions(container)
        this.#showCompanyInfoSection()
      })

      container.appendChild(li)
      container.classList.add("show")
      this.#positionDropdown(container)
      return
    }

    container.setAttribute("role", "listbox")

    data.forEach((item) => {
      if (!item.company_name) return
      const tpl = this.itemTemplateTarget.content.cloneNode(true)
      const li = tpl.querySelector("li")
      li.setAttribute("role", "option")
      li.setAttribute("aria-selected", "false")

      tpl.querySelector(".dropdown-item__legal-name").innerText = item.company_name
      tpl.querySelector(".dropdown-item__registration-id").innerText = this.#identifierLine(item)
      tpl.querySelector(".dropdown-item__city").innerText = item.municipality || ""
      this.#fillSourceLine(tpl, item)

      li.addEventListener("click", () => {
        this.#selectOption(item, container, request)
        this.#showCompanyInfoSection()
      })

      container.appendChild(li)
    })
    container.classList.add("show")
    this.#positionDropdown(container)
  }

  // A VIES record has a VAT id and often no registration number — showing
  // "IČO: undefined" there is what the identifier line used to do.
  #identifierLine(item) {
    if (item.registration_number) return `${this.registrationNumberLabelValue}: ${item.registration_number}`
    if (item.vat_id) return `${this.registryConfigValue.vatIdLabel || "VAT ID"}: ${item.vat_id}`
    return ""
  }

  // Names the source when it is not the country's company register (VIES), so
  // the user knows the record lists VAT payers and why fields may be missing.
  #fillSourceLine(tpl, item) {
    const line = tpl.querySelector(".dropdown-item__source")
    if (!line) return

    const label = this.registryConfigValue.sources?.[item.source]
    if (label) {
      line.innerText = label
    } else {
      line.remove()
    }
  }

  #positionDropdown(container) {
    const anchorRect = this.#dropdownAnchor(container)?.getBoundingClientRect()
    if (!anchorRect) return

    const gap = 6
    const boundaryRect = this.#dropdownBoundary(container)
    const preferredMaxHeight = this.#preferredDropdownMaxHeight(container)
    const availableBelow = Math.max(boundaryRect.bottom - anchorRect.bottom - gap, 0)
    const availableAbove = Math.max(anchorRect.top - boundaryRect.top - gap, 0)
    const preferredMenuHeight = Math.min(container.scrollHeight, preferredMaxHeight)
    const openUp = availableBelow < preferredMenuHeight && availableAbove > availableBelow
    const availableHeight = openUp ? availableAbove : availableBelow
    const maxHeight = Math.max(Math.min(availableHeight, preferredMaxHeight), 0)
    const viewportOffset = this.#visualViewportOffset()

    container.style.left = `${anchorRect.left + viewportOffset.left}px`
    container.style.width = `${anchorRect.width}px`
    container.style.minWidth = `${anchorRect.width}px`
    container.style.maxWidth = `${anchorRect.width}px`
    container.style.maxHeight = `${maxHeight}px`

    const menuHeight = Math.min(container.scrollHeight, maxHeight)
    const top = openUp ? Math.max(boundaryRect.top, anchorRect.top - menuHeight - gap) : anchorRect.bottom + gap

    container.style.top = `${top + viewportOffset.top}px`

    container.classList.toggle("dropdown-menu-up", openUp)
  }

  #dropdownBoundary(container) {
    const viewportRect = this.#visibleViewportRect()
    const modalBody = container.closest(".modal-body")
    if (modalBody) return this.#intersectRects(modalBody.getBoundingClientRect(), viewportRect)

    const card = container.closest(".ob-card")
    if (card) return this.#intersectRects(card.getBoundingClientRect(), viewportRect)

    const scrollContainer = container.closest(".ob-section-scroll, .page__body, .page, .onboarding")
    return scrollContainer ? this.#intersectRects(scrollContainer.getBoundingClientRect(), viewportRect) : viewportRect
  }

  #dropdownAnchor(container) {
    return container.closest(".lc-autocomplete")?.querySelector("[data-legal-entity-target~='search']")
  }

  #preferredDropdownMaxHeight(container) {
    const computedMaxHeight = parseFloat(window.getComputedStyle(container).maxHeight)
    return Number.isFinite(computedMaxHeight) ? computedMaxHeight : 384
  }

  #visibleViewportRect() {
    if (this.visualViewport) {
      return {
        top: 0,
        left: 0,
        right: this.visualViewport.width,
        bottom: this.visualViewport.height
      }
    }

    return {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    }
  }

  #visualViewportOffset() {
    if (!this.visualViewport) {
      return { top: 0, left: 0 }
    }

    return {
      top: this.visualViewport.offsetTop,
      left: this.visualViewport.offsetLeft
    }
  }

  #intersectRects(rect, viewportRect) {
    const top = Math.max(rect.top, viewportRect.top)
    const left = Math.max(rect.left, viewportRect.left)
    const right = Math.min(rect.right, viewportRect.right)
    const bottom = Math.min(rect.bottom, viewportRect.bottom)

    if (bottom <= top || right <= left) {
      return viewportRect
    }

    return { top, left, right, bottom }
  }

  repositionDropdown(event) {
    if (this.#shouldIgnoreRepositionEvent(event)) return
    if (this.repositionFrame) window.cancelAnimationFrame(this.repositionFrame)

    this.repositionFrame = window.requestAnimationFrame(() => {
      if (this.hasCompanyNameListTarget && this.companyNameListTarget.classList.contains("show")) {
        this.#positionDropdown(this.companyNameListTarget)
      }

      this.repositionFrame = null
    })
  }

  clearOptions(container) {
    this.activeIndex = -1
    container.innerHTML = ""
    container.classList.remove("show", "dropdown-menu-up")
    container.style.removeProperty("left")
    container.style.removeProperty("top")
    container.style.removeProperty("width")
    container.style.removeProperty("min-width")
    container.style.removeProperty("max-width")
    container.style.removeProperty("max-height")
  }

  #selectOption(item, container, request = null) {
    this.clearOptions(container)
    this.clearFields()
    this.#populateFields(item, request)
    // Cross-controller signal for contacts--disclosure (and any future
    // listener) — same document-level CustomEvent convention already used by
    // "selectedContact"/"changeContact" in invoices/select_contact_controller.
    document.dispatchEvent(new CustomEvent("registry:selected", { detail: { item } }))
  }

  #showCompanyInfoSection() {
    if (this.hasCompanyInfoSectionTarget) {
      this.companyInfoSectionTarget.classList.remove("on-section__data--hidden")
      this.companyInfoSectionTarget.classList.add("on-section__data--visible")
    }
  }

  // FR-6: a country we have no register for drops STRAIGHT into manual entry
  // with the fields visible. #showCompanyInfoSection above only knows
  // onboarding's own disclosure (`on-section__data--hidden`); the contact modal
  // uses contacts--disclosure, which nothing was telling. So the notice
  // appeared over a form that stayed collapsed behind two escape buttons, and
  // the user had to click "enter manually" to reach fields we had just told
  // them were the only option.
  //
  // Same document-level CustomEvent as registry:selected — the reveal is
  // identical, only the reason differs.
  #requireManualEntry() {
    document.dispatchEvent(new CustomEvent("registry:manual-required"))
  }

  #populateFields(data, request = null) {
    // The country the data actually came FROM, not whatever the form shows now.
    const countryId = request?.countryId ?? this.#selectedCountryId()

    this.#fillTargets(this.nameTargets, "company_name", data)
    // Filling an input programmatically fires no event, so the logo lookup bound
    // to `change` on this field never re-ran and kept whatever it guessed from
    // the raw term — for an IČO search that meant Logo.dev was queried for a
    // company literally named "52070999", and that URL is what got saved.
    // `change` only reaches the logo controller here; legal-entity#search is
    // bound to `input`, so this cannot loop.
    this.nameTargets?.forEach((el) => el.dispatchEvent(new Event("change", { bubbles: true })))

    this.#fillTargets(this.registrationIdTargets, "registration_number", data)
    this.#fillTargets(this.taxIdTargets, "tax_id", data)
    this.#fillTargets(this.vatIdTargets, "vat_id", data)

    const vatPayerTypeSet = this.setVatPayerType(data.vat_register_type, this.#valueOf("vat_id", data))

    if (this.hasLegalFormTarget) {
      const legalFormOption = this.#legalFormOptionForRegistryCode(data.legal_form_code)
      if (legalFormOption) {
        this.legalFormTarget.value = legalFormOption.value
        this.legalFormTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    this.#fillAddress(data)

    if (this.hasCountryTarget && countryId) {
      this.countryTargets.forEach((el) => this.#setSelectValue(el, countryId))
    }

    this.#fillTargets(this.postalCodeTargets, "postal_codes", data)

    this.#fillRegisterText(data)

    this.populatedIdentity = { name: this.#valueOf("company_name", data), countryId }

    // Reveal the origin marker for every field we just actually wrote a value
    // into — and only those: a field the registry had nothing for is left
    // empty above, so its marker stays hidden rather than claiming an origin
    // for a value that isn't there.
    this.#revealProvenanceMarkers(vatPayerTypeSet)
    this.#updateSummaryBanner(data)
  }

  #fillTargets(targets, prop, data) {
    if (!targets) return
    const value = this.#valueOf(prop, data)
    targets.forEach((el) => (el.value = value))
  }

  #valueOf(prop, data) {
    return Array.isArray(data[prop]) ? (data[prop][0] ? data[prop][0] : "") : data[prop] || ""
  }

  #fillAddress(data) {
    if (!this.hasStreetTarget) return
    const full = getFormattedStreet(
      data.municipality || "",
      data.street || "",
      data.reg_number || "",
      data.building_number || ""
    )
    this.streetTargets.forEach((el) => (el.value = full))
    // `data.municipality` is the registry normalized-hash wire-key (kept as-is).
    this.cityTargets.forEach((el) => (el.value = data.municipality || ""))
  }

  #fillRegisterText(data) {
    if (!this.hasRegisterTextTarget) return
    // A register that hands over its own line (ANAF's "Nr. Reg. Com. …") is
    // used as it is; the composition below is the Slovak register's format.
    if (data.register_text) {
      this.registerTextTargets.forEach((el) => (el.value = data.register_text))
      return
    }
    const {
      source_register_value: val,
      source_register_registration_office: office,
      source_register_registration_number: num
    } = data
    if (!val || !office || !num) return

    let text = `${val} ${office}`
    if (val === "Obchodný register") {
      const [odd, vloz, char] = num.split("/")
      text += `, oddiel: ${odd}, vložka č. ${vloz}/${char}`
    } else {
      text += `, registrácia č. ${num}`
    }
    this.registerTextTargets.forEach((el) => (el.value = text))
  }

  // Returns whether this call actually associated a party[vat_payer_type]
  // radio with the registry's answer — true whether or not the radio needed
  // to flip (an already-correct radio still IS the registry's answer), false
  // when there's no matching radio on this page at all, or the type couldn't
  // be mapped. #populateFields uses this to decide whether the
  // vat_payer_type provenance marker should be revealed — that field has no
  // single DOM id of its own (it's a radio group), so it can't be covered by
  // the generic getElementById reveal loop in #revealProvenanceMarkers.
  //
  // The § table is the server's (LegalEntityRegistry::VatRegisterType::MAP via
  // registry_search_config), so this and the daily register check read a
  // paragraph the same way. A § 7/§ 7a answer on a form with no "foreign" radio
  // changes nothing and says so: falling back to non-payer saved a § 7a
  // company as "neplatca DPH" (ws 357). Likewise a blank paragraph next to an
  // IČ DPH says nothing about which kind of payer it is — the daily check's
  // comparator refuses to guess there too (CompanyRegister::Comparator
  // #register_vat_payer_type), so only a blank paragraph WITHOUT one is a
  // non-payer.
  setVatPayerType(type, vatId = null) {
    const map = {
      ...this.registryConfigValue.vatRegisterTypes,
      standard: "standard",
      foreign: "foreign",
      non_payer_vat: "non_payer_vat"
    }
    const key = String(type ?? "").replace(/\s/g, "")
    if (key === "" && String(vatId ?? "").trim() !== "") {
      this.#showVatTypeNotice(this.registryConfigValue.vatTypeUnknown)
      return false
    }

    const val = key === "" ? "non_payer_vat" : Object.hasOwn(map, key) ? map[key] : undefined
    if (!val) {
      // An older notice described an earlier pick, not this one.
      this.#hideVatTypeNotice()
      console.error("Invalid VAT type", type)
      return false
    }

    const vatPayerTypeRadio = this.element.querySelector(`input[name='party[vat_payer_type]'][value='${val}']`)
    if (!vatPayerTypeRadio) {
      if (val === "foreign") this.#showVatTypeNotice(this.registryConfigValue.vatTypeUnavailable)
      return false
    }

    if (!vatPayerTypeRadio.checked) vatPayerTypeRadio.checked = true
    this.#hideVatTypeNotice()
    return true
  }

  // The notice sits inside turbo-frame#vat_payer_type, which #populateFields'
  // legal-form `change` re-renders right after setVatPayerType — through a
  // navigate turbo-stream `replace` (WorkspacesController#render_turbo_streams),
  // which fires no turbo:frame-render. The server's partial brings the notice
  // back empty, so the text lives here and is written again into whichever
  // notice element connects next, however the frame was replaced.
  //
  // The element stays in the DOM (role="status") and only its TEXT changes: a
  // live region revealed from display:none is not announced by most screen
  // readers. Empty, it is an empty block and takes no space.
  vatTypeNoticeTargetConnected(element) {
    if (this.pendingVatTypeNotice) element.textContent = this.pendingVatTypeNotice
  }

  #showVatTypeNotice(text) {
    this.pendingVatTypeNotice = text || ""
    if (!this.hasVatTypeNoticeTarget) return

    this.vatTypeNoticeTarget.textContent = this.pendingVatTypeNotice
  }

  #hideVatTypeNotice() {
    this.pendingVatTypeNotice = ""
    if (!this.hasVatTypeNoticeTarget) return

    this.vatTypeNoticeTarget.textContent = ""
  }

  clearFields() {
    this.nameTargets?.forEach((el) => (el.value = ""))
    this.#clearDependentFields()
  }

  // Everything a registry lookup fills EXCEPT the name — the name is what the
  // user is typing, so it must survive an invalidation.
  #clearDependentFields() {
    const groups = [
      this.registrationIdTargets,
      this.vatIdTargets,
      this.taxIdTargets,
      this.streetTargets,
      this.cityTargets,
      this.postalCodeTargets,
      this.registerTextTargets
    ]
    groups.forEach((group) => group?.forEach((el) => (el.value = "")))

    const vatPayerTypeRadio = this.element.querySelector("input[name='party[vat_payer_type]']")
    if (vatPayerTypeRadio) vatPayerTypeRadio.checked = true
    // The § 7/§ 7a notice described the cleared pick, not whatever comes next.
    this.#hideVatTypeNotice()

    // Every REGISTRY marker (including the name's, since the identity it named
    // no longer matches this term) and the summary banner claim the register as
    // the origin of this exact set of values — once those values are gone, so
    // is the claim. Markers from another origin claim nothing on our behalf.
    this.#hideAllProvenanceMarkers()
  }

  // Reveal the origin marker for every field #populateFields actually wrote a
  // value into. Markers are keyed by `data-field` = the DOM id of the input
  // they describe (see shared/registry/_provenance_marker.html.erb and
  // RegistryHelper#provenance_marker) — that id is the one join between "which
  // field did this fill" and "which marker says so", so a future field only
  // needs a marker declared with the matching id to be covered here for free.
  //
  // party_vat_payer_type is the one exception: a radio GROUP has no single
  // element whose id equals the group's name, so no id lookup can ever find it
  // — its marker is revealed from `vatPayerTypeSet`, the boolean
  // #populateFields got back from setVatPayerType, instead.
  #revealProvenanceMarkers(vatPayerTypeSet = false) {
    this.#registryMarkers().forEach((marker) => {
      if (marker.dataset.field === "party_vat_payer_type") {
        marker.classList.toggle("d-none", !vatPayerTypeSet)
        return
      }

      const input = this.#fieldById(marker.dataset.field)
      marker.classList.toggle("d-none", !input?.value)
    })
  }

  #hideAllProvenanceMarkers() {
    this.#registryMarkers().forEach((marker) => {
      marker.classList.add("d-none")
    })
    if (this.hasSummaryBannerTarget) this.summaryBannerTarget.classList.add("d-none")
  }

  #countVisibleProvenanceMarkers() {
    return this.#registryMarkers().filter((marker) => !marker.classList.contains("d-none")).length
  }

  // A registry pick only ever speaks for the values IT wrote. Other origins mark
  // their own fields with the same partial — the expense form's :ai star for
  // address_name sits inside this very controller's element, because
  // legal-entity is declared on the whole <form> there — and revealing or
  // hiding those would credit the register for an AI-extracted value, or drop a
  // star the server still stands behind (extraction_strategy).
  #registryMarkers() {
    const selector = "[data-contacts--provenance-target='marker'][data-source='registry']"
    return Array.from(this.element.querySelectorAll(selector))
  }

  // Scoped to this controller, not the document: on realities/new and
  // realities/edit the property's OWN address_name/street/city/postal_code
  // inputs are rendered before the contact modal's turbo-frame, so a global
  // getElementById handed us the property's field and judged the modal's marker
  // by a value the register never wrote.
  #fieldById(id) {
    return id ? this.element.querySelector(`[id="${id}"]`) : null
  }

  // The register name is only known from the response we just got — the
  // per-field markers are pre-rendered by the server with generic copy and
  // can't carry it, so this is the one place the actual register name (vs a
  // generic fallback) ever reaches the user.
  #updateSummaryBanner(data) {
    if (!this.hasSummaryBannerTarget) return

    const filledCount = this.#countVisibleProvenanceMarkers()
    if (filledCount === 0) {
      this.summaryBannerTarget.classList.add("d-none")
      return
    }

    const registerLabel =
      data.source_register_value || this.registryConfigValue.sources?.[data.source] || this.registerFallbackTextValue
    this.summaryTitleTarget.textContent = `${registerLabel} — ${filledCount} ${this.#pluralizeFieldsCount(filledCount)}`
    this.summaryBannerTarget.classList.remove("d-none")
  }

  #pluralizeFieldsCount(count) {
    return pluralText(this.fieldsCountTextsValue, this.langValue, count)
  }

  clearSearch() {
    this.#invalidatePendingSearch()

    if (this.hasCompanyNameListTarget) {
      this.clearOptions(this.companyNameListTarget)
    }

    this.populatedIdentity = null
    this.clearFields()
    this.updateStatus(false)
  }

  // Renders into the dropdown — the one element every surface has — as well as
  // the optional error target. The dropdown row matters twice over: it is the
  // only way most surfaces can show a failure at all, and it replaces the
  // "Searching…" row, which updateStatus(false) does not clear.
  showError(key) {
    const messages = {
      registry_unavailable: this.registryUnavailableTextValue,
      registry_lookup_limit: this.registryConfigValue.limitReached
    }
    const message = messages[key] || this.registryErrorTextValue

    this.#showDropdownMessage(message)

    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  showMinCharactersMessage() {
    this.#showDropdownMessage(this.minCharactersTextValue)
  }

  // Show the message in the dropdown list like the "not found" message
  #showDropdownMessage(message) {
    if (!this.hasCompanyNameListTarget) return

    const container = this.companyNameListTarget
    this.clearOptions(container)

    const li = document.createElement("li")
    li.textContent = message
    li.classList.add("dropdown-item", "dropdown-item--empty")
    li.addEventListener("click", () => this.clearOptions(container))

    container.appendChild(li)
    container.classList.add("show")
    this.#positionDropdown(container)
  }

  #syncInputAffordances(loading = false) {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("d-none", loading || !this.#searchValue())
    }

    const registerAvailable = this.#registerAvailable()
    const copy = this.#countryCopy()

    this.searchTargets.forEach((input) => {
      // The placeholder says what the search takes in this country — ID or
      // name, only a CUI, only a VAT id — and nothing where there is no
      // register or for a private person. Country and entity type both change
      // client-side, so the server's first paint goes stale on the very field
      // where search() deliberately returns early.
      input.setAttribute("placeholder", registerAvailable ? copy.placeholder || "" : "")

      const wrapper = input.closest(".lc-autocomplete")
      if (!wrapper) return

      const showSearchIcon = !loading && !input.value.trim() && registerAvailable
      wrapper.classList.toggle("lc-autocomplete--show-search-icon", showSearchIcon)
    })

    // Same promise in prose ("we'll fill the details from the company
    // register") — only true for a full register. Elsewhere the notice under
    // the field says what the search can do instead, and a private-person
    // name field promises nothing.
    const fullRegister = registerAvailable && copy.level === "full"
    this.searchHintTargets.forEach((el) => el.classList.toggle("d-none", !fullRegister))
  }

  #activeSearchInput() {
    return this.searchTargets.find((el) => el === document.activeElement) || this.searchTargets[0]
  }

  #searchValue() {
    return this.#activeSearchInput()?.value.trim() || ""
  }

  #selectedCountryId() {
    if (this.hasCountryDeciderTarget) return this.countryDeciderTarget.value
    if (this.hasCountryTarget) return this.countryTarget.value

    return null
  }

  #legalFormOptionForRegistryCode(code) {
    const normalizedCode = String(code)
    return Array.from(this.legalFormTarget.options).find((option) => option.dataset.legalFormCode === normalizedCode)
  }

  #setSelectValue(el, value) {
    el.value = value
    const wrapper = el.closest('[data-controller~="searchable-select"]')
    if (wrapper) {
      const ssController = this.application.getControllerForElementAndIdentifier(wrapper, "searchable-select")
      ssController?.slimSelect?.setSelected(String(value))
    }
  }

  #shouldIgnoreRepositionEvent(event) {
    if (!event || event.type !== "scroll") return false
    if (event.target === window || event.target === document || event.target === document.documentElement) return false
    if (event.target === this.visualViewport) return false

    return event.target instanceof Element && !!event.target.closest(".lc-autocomplete .dropdown-menu")
  }
}
